import dayjs from "dayjs";
import db from "@/db";
import { recordKey } from "@/db/pendingRecord";
import { useUsersStore } from "@/store/modules/users";
import { useNetworkStore } from "@/composables/network/useNetwork";
import { triggerBrowserDownload } from "@/utils/fileUtils";

const recordData = async (record) => {
  try {
    return await record.asForm();
  } catch (formError) {
    try {
      return structuredClone({ ...record });
    } catch {
      return { unreadable: String(formError) };
    }
  }
};

const modelOf = (table) =>
  Object.values(db.models).find(({ tableName }) => tableName === table);

const relatedRecords = async (table, record, pendingKeys) => {
  const links = Object.values(modelOf(table)?.relatedFields ?? {}).filter(
    (link) => link.field && !link.many && !link.m2m && db[link.tableName]
  );
  const related = await Promise.all(
    links.map(async ({ field, tableName }) => {
      const uuid = record[field];
      if (typeof uuid !== "string" || !uuid) return null;
      const linked = await db[tableName].get(uuid);
      return {
        field,
        table: tableName,
        uuid,
        pending: pendingKeys.has(recordKey(tableName, uuid)),
        data: linked ? await recordData(linked) : null,
      };
    })
  );
  return related.filter(Boolean);
};

const describeChange = async (row, projects, pendingKeys) => {
  const record = await db[row.table]?.get({ uuid: row.uuid });
  return {
    key: row.key,
    project: projects.get(row.projectUuid) ?? { uuid: row.projectUuid },
    table: row.table,
    uuid: row.uuid,
    kind: record?.isCreatedOffline ? "create" : "update",
    keptAt: dayjs(row.at).toISOString(),
    page: row.page,
    skeleton: !!row.skeleton,
    rejected: row.rejected ?? null,
    failed: row.failed ?? null,
    data: record ? await recordData(record) : null,
    related: record ? await relatedRecords(row.table, record, pendingKeys) : [],
  };
};

const deviceContext = () => {
  const { isOnline, isOfflineMode, lastServerReachAt } = useNetworkStore();
  return {
    databaseVersion: db.verno,
    userAgent: globalThis.navigator.userAgent,
    online: isOnline,
    offlineMode: isOfflineMode,
    lastServerReachAt: lastServerReachAt
      ? dayjs(lastServerReachAt).toISOString()
      : null,
  };
};

export const pendingChangesPayload = async ({ projectUuid } = {}) => {
  const rows = await (
    projectUuid ? db.pendingSyncs.where({ projectUuid }) : db.pendingSyncs
  ).toArray();
  const pendingKeys = new Set(rows.map(({ key }) => key));
  const projectRows = await db.projects
    .where("uuid")
    .anyOf([...new Set(rows.map(({ projectUuid }) => projectUuid))])
    .toArray();
  const projects = new Map(
    projectRows.map(({ uuid, projectName, chronorapso }) => [
      uuid,
      { uuid, projectName, chronorapso },
    ])
  );
  return {
    application: "atmo-front",
    version: import.meta.env.VITE_APP_VERSION ?? null,
    exportedAt: dayjs().toISOString(),
    user: {
      uuid: useUsersStore().currentUserUuid ?? null,
      email: useUsersStore().currentUserEmail ?? null,
    },
    device: deviceContext(),
    changes: await Promise.all(
      rows.map((row) => describeChange(row, projects, pendingKeys))
    ),
  };
};

export const exportPendingChanges = async () => {
  const payload = await pendingChangesPayload();
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  triggerBrowserDownload(
    url,
    `atmo-modifications-en-attente-${dayjs().format("YYYYMMDD-HHmm")}.json`
  );
  return payload.changes.length;
};
